import {
  useCreateNewsItem,
  useDeleteNews,
  useUpdateNewsItem,
} from "@/api/cms.ts";
import { Button } from "@/components/ui/button.tsx";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import useMediaAttachment from "@/hooks/useMediaAttachment";
import { getMainLanguage, SupportedLocale } from "@/i18n";
import { NewsItem, newsItemSchema } from "@/lib/cms/cms-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trash2 } from "lucide-react";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CroppedImageInput } from "../form-fields/CroppedImageInput";
import { Col, RichText, SelectField, Text } from "../form-items/AbstractForm";
import { MultilingualField } from "../form-items/MultilingualField";
import { generateImageName } from "@/lib/utils";
import { Input } from "../ui/input";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";

const newsSchemas = newsItemSchema
  .pick({
    title: true,
    content: true,
    excerpt: true,
    date: true,
    status: true,
  })
  .extend({
    // Older news rows may have excerpt: null from Prisma
    excerpt: newsItemSchema.shape.excerpt.nullable().optional(),
  });

type FormData = z.infer<typeof newsSchemas>;

const NewsForm = ({
  newsItem,
  refetch,
  onSuccess,
}: {
  newsItem?: NewsItem;
  refetch: () => void;
  /**
   * Après create / update / delete réussis (ex. fermer le drawer).
   * Sur create/update, reçoit l'item renvoyé par l'API quand disponible.
   */
  onSuccess?: (item?: NewsItem) => void;
}) => {
  const { t } = useTranslation();
  const cmsNew = useCmsNew();
  const { instanceData } = useAuth();

  // On create, images are uploaded with docId = instanceId; the API dirty-fix
  // re-links them to the new news id on POST. Preview therefore uses instanceId
  // until the news exists.
  const mediaDocId = newsItem?.id ?? instanceData?.id;

  const {
    attachment: newsAttachment,
    previewImageUrl,
    refetch: refetchNewsMedia,
  } = useMediaAttachment("newsImage", mediaDocId);

  const defaultValues: FormData = useMemo(
    () => ({
      title: newsItem?.title ?? {},
      content: newsItem?.content ?? {},
      // Prisma may return null for excerpt on older rows — zod record rejects null.
      excerpt: newsItem?.excerpt ?? {},
      date: newsItem?.date ? new Date(newsItem.date) : new Date(),
      status: newsItem?.status ?? "DRAFT",
    }),
    [newsItem],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(newsSchemas),
    defaultValues,
  });

  const { handleSubmit } = form;

  const create = useCreateNewsItem();
  const update = useUpdateNewsItem();
  const deleteNews = useDeleteNews();

  const displayAttachment =
    newsAttachment ??
    (newsItem?.id != null ? newsItem.attachments?.[0] : undefined);
  const displayPreviewUrl =
    previewImageUrl ??
    (newsItem?.id != null
      ? newsItem.attachments?.[0]?.mediumPublicUrl
      : undefined);

  const handleDelete = (newsId: string) => {
    deleteNews.mutate(newsId, {
      onSuccess: () => {
        toast({
          title: t("success"),
          description: t("newsDeleteSuccessMessage"),
          duration: 1000,
          variant: "success",
        });
        form.reset();
        refetch();
        onSuccess?.();
      },
      onError: () => {
        toast({
          title: t("error"),
          description: t("newsDeleteErrorMessage"),
          duration: 1000,
          variant: "error",
        });
      },
    });
  };

  const onSubmit = (data: FormData) => {
    const mutationMethod = newsItem?.id ? update : create;

    if (instanceData == null) return;

    mutationMethod.mutate(
      {
        id: newsItem?.id,
        ...data,
        excerpt: data.excerpt ?? {},
      },
      {
        onSuccess: (saved) => {
          toast({
            title: t("success"),
            description: t("newsSuccessMessage"),
            duration: 1000,
            variant: "success",
          });
          void refetchNewsMedia();
          refetch();
          if (!newsItem?.id) {
            form.reset({
              title: {},
              content: {},
              excerpt: {},
              date: new Date(),
              status: "DRAFT",
            });
          }
          onSuccess?.(saved as NewsItem | undefined);
        },
        onError: () => {
          toast({
            title: t("error"),
            description: t("newsErrorMessage"),
            duration: 1000,
            variant: "error",
          });
        },
      },
    );
  };

  // Only reset when switching to another news item — not on every refetch,
  // which would wipe in-progress edits and make "save" appear to do nothing.
  useEffect(() => {
    form.reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on id
  }, [newsItem?.id]);

  const formatDateInput = (val: unknown): string => {
    if (!val) return "";
    if (val instanceof Date) return val.toISOString().split("T")[0];
    if (typeof val === "string") return val.split("T")[0];
    return "";
  };

  const handleMediaRefetch = () => {
    refetchNewsMedia();
    refetch();
  };

  return (
    <div className="gap-6 flex flex-col overflow-y-auto overflow-x-hidden min-w-0 max-w-full">
      <CroppedImageInput
        label={t("cms.newsItem.form.media")}
        docId={mediaDocId}
        imageURL={displayPreviewUrl}
        attachment={displayAttachment}
        refetch={handleMediaRefetch}
        imagePosition="newsImage"
        aspect={16 / 9}
        buildImageName={(instanceName) =>
          generateImageName({
            primaryType: "thumbnail",
            instanceName,
            pageNameInDefaultLanguage: "news",
            title: newsItem?.title?.[getMainLanguage()],
          })
        }
      />

      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="gap-6 flex flex-col">
          <SelectField
            name="status"
            label={t("cms.newsItem.form.status")}
            options={[
              {
                label: t("cms.newsItem.form.statusDraft"),
                value: "DRAFT",
              },
              {
                label: t("cms.newsItem.form.statusPublished"),
                value: "PUBLISHED",
              },
            ]}
          />
          <MultilingualField
            defaultLang={getMainLanguage() as SupportedLocale}
            render={(lang) => (
              <Col>
                <Text
                  name={`title.${lang}`}
                  label={t("cms.newsItem.form.title")}
                />
                <Text
                  name={`excerpt.${lang}`}
                  label={cmsNew("cms.new.news.form.excerpt", "Extrait")}
                />
                <RichText
                  name={`content.${lang}`}
                  label={t("cms.newsItem.form.content")}
                />
              </Col>
            )}
          />
          <Controller
            control={form.control}
            name="date"
            render={({ field }) => (
              <Input
                label={t("cms.newsItem.form.date")}
                type="date"
                value={formatDateInput(field.value)}
                onChange={(e) => {
                  const value = e.target.value;
                  field.onChange(value ? new Date(value) : undefined);
                }}
              />
            )}
          />

          {newsItem?.id != undefined && (
            <Button
              className="flex gap-1 text-sm items-center text-red-600 justify-center cursor-pointer"
              variant="not_selected"
              type="button"
              onClick={() => handleDelete(newsItem.id as string)}
            >
              <Trash2 className={"w-4 h-4"} />
              <p>{t("delete")}</p>
            </Button>
          )}
          <hr />
          <Button type="submit">{t("addNewsSubmitButton")}</Button>
        </form>
      </FormProvider>
    </div>
  );
};

export default NewsForm;
